import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import Breadcrumb from "@/components/base/Breadcrumb";
import FaqAccordion from "@/components/base/FaqAccordion";
import { faqItems } from "@/mocks/content";
import { SITE } from "@/config/site";
import { cn } from "@/utils/format";

const groups = ["全部问题", "观看与信号", "连麦与投稿", "内容与版权"];

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer },
    })),
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: `${SITE.url}/` },
      { "@type": "ListItem", position: 2, name: "常见问题", item: `${SITE.url}/faq` },
    ],
  },
];

export default function Faq() {
  const [group, setGroup] = useState("全部问题");
  const [keyword, setKeyword] = useState("");

  const filtered = useMemo(() => {
    return faqItems.filter((item) => {
      const matchGroup = group === "全部问题" || item.group === group;
      const text = `${item.question}${item.answer}`;
      const matchKeyword = keyword.trim() === "" || text.includes(keyword.trim());
      return matchGroup && matchKeyword;
    });
  }, [group, keyword]);

  const grouped = useMemo(() => {
    const map = new Map<string, typeof faqItems>();
    filtered.forEach((item) => {
      const list = map.get(item.group) ?? [];
      list.push(item);
      map.set(item.group, list);
    });
    return Array.from(map.entries());
  }, [filtered]);

  return (
    <>
      <Seo
        title="常见问题｜观看、连麦与信号答疑 - 爆料直播"
        description="爆料直播常见问题解答：现场实时画面是否有延时、为什么有些直播没有声音、直播卡顿如何恢复、回看保留多久、如何申请连麦与提交现场素材、内容转载与更正流程等。"
        keywords="爆料直播常见问题,实时画面信号,连麦申请,现场投稿,回看保留时长"
        path="/faq"
        jsonLd={jsonLd}
      />

      <section className="relative overflow-hidden border-b border-background-200/70 pt-16 md:pt-20">
        <div className="stage-grid absolute inset-0 opacity-60" aria-hidden="true" />
        <div className="relative w-full px-4 py-10 md:px-6 md:py-14">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "常见问题" }]} />
          <h1 className="mt-6 max-w-3xl font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
            常见问题：观看、连麦与信号说明
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            关于实时画面信号的延时、声音开关、连麦申请与回看时长，这里做了完整说明。
            如果没有找到你的问题，可以通过邮箱联系编辑组。
          </p>

          <div className="mt-7 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              {groups.map((name) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => setGroup(name)}
                  aria-pressed={group === name}
                  className={cn(
                    "cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-[13px] transition-colors",
                    group === name
                      ? "bg-primary-500 text-background-50"
                      : "border border-background-300/60 text-foreground-700 hover:border-primary-500/60 hover:text-primary-500"
                  )}
                >
                  {name}
                </button>
              ))}
            </div>
            <div className="flex w-full items-center gap-2 rounded-md border border-background-300/60 bg-background-100 px-3 py-2.5 lg:max-w-xs focus-within:border-primary-500/70">
              <i className="ri-search-line text-base text-foreground-500" aria-hidden="true" />
              <label htmlFor="faq-search" className="sr-only">
                搜索问题
              </label>
              <input
                id="faq-search"
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索关键词，例如“连麦”"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50 px-4 py-12 md:px-6 md:py-16">
        {grouped.length > 0 ? (
          <div className="flex flex-col gap-10">
            {grouped.map(([name, list]) => (
              <div key={name}>
                <div className="mb-4 flex items-center gap-3">
                  <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
                    {name}
                  </h2>
                  <span className="font-label text-[11px] text-foreground-500">
                    {list.length} 个问题
                  </span>
                </div>
                <FaqAccordion items={list} />
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-background-300/70 bg-background-100/50 px-6 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200/70 text-foreground-500">
              <i className="ri-question-mark text-xl" aria-hidden="true" />
            </span>
            <h2 className="font-heading text-lg font-bold text-foreground-950">
              没有匹配的问题
            </h2>
            <p className="max-w-md text-[13px] leading-relaxed text-foreground-600">
              换个关键词试试，或者直接发邮件给编辑组，我们会把高频问题补充到这一页。
            </p>
          </div>
        )}

        <div className="mt-12 flex flex-col items-center justify-between gap-5 rounded-lg border border-background-200/70 bg-background-100 p-6 text-center sm:flex-row sm:text-left md:p-8">
          <div>
            <h2 className="font-heading text-lg font-bold text-foreground-950">
              还有别的问题想问？
            </h2>
            <p className="mt-1.5 text-[13px] leading-relaxed text-foreground-600">
              发邮件给编辑组，涉及投稿与连麦的来信会优先处理。
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <a
              href={`mailto:${SITE.email}`}
              className="inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-mail-send-line" aria-hidden="true" />
              {SITE.email}
            </a>
            <Link
              to="/live"
              className="inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300/60 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-500/60 hover:text-primary-500"
            >
              <i className="ri-live-line" aria-hidden="true" />
              去看正在直播
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}